/**
 * timeEntryEdit.js: correcting a time entry (IDI-473).
 *
 * The bug: the correction UI edits HH:mm only, and saved that bare "16:30" as
 * clockIn/clockOut. `new Date("16:30")` is Invalid, so the row read "—" and the
 * entry fell out of the total. Live on tenant 1 (2026-10-14): an entry stored
 * clockIn "07:00" with the real date only in originalClockIn.
 *
 * The fix rebuilds a full ISO timestamp from the entry's ORIGINAL date plus
 * the edited time, in the user's local timezone (the picker shows local time).
 */

const HHMM = /^([01]?\d|2[0-3]):([0-5]\d)$/;

/** A Date from a stored value, or null. A bare "HH:mm" is not a date. */
export function parseStamp(v) {
  if (v == null || v === '') return null;
  if (typeof v === 'string' && HHMM.test(v.trim())) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * The calendar day an entry belongs to: its clock-in, else its clock-out, else
 * the original stamps a previous broken correction left behind.
 */
function entryDay(entry) {
  return parseStamp(entry?.clockIn)
    || parseStamp(entry?.clockOut)
    || parseStamp(entry?.originalClockIn)
    || parseStamp(entry?.originalClockOut)
    || null;
}

/** "HH:mm" (local) for a stored value, for the picker. A bare time passes through. */
export function toHHMM(v) {
  if (typeof v === 'string' && HHMM.test(v.trim())) {
    const [, h, m] = v.trim().match(HHMM);
    return `${h.padStart(2, '0')}:${m}`;
  }
  const d = parseStamp(v);
  if (!d) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** ISO stamp for `hhmm` on the local calendar day of `day`. */
export function combine(day, hhmm) {
  const m = typeof hhmm === 'string' ? hhmm.trim().match(HHMM) : null;
  if (!day || !m) return null;
  const d = new Date(day.getFullYear(), day.getMonth(), day.getDate(), Number(m[1]), Number(m[2]), 0, 0);
  return d.toISOString();
}

/**
 * Build the corrected entry, or explain why not.
 *
 * Clock-out earlier than clock-in rolls to the NEXT day only when the original
 * entry spanned midnight; otherwise it's refused ("Clock-out must be after
 * clock-in"), which is what a user who typed 16:30 for 17:00 by mistake needs.
 *
 * @returns {{ ok: true, entry: object } | { ok: false, error: string }}
 */
export function correctTimeEntry(entry, { clockIn, clockOut }, { by, now = new Date() } = {}) {
  const day = entryDay(entry);
  if (!day) return { ok: false, error: 'This entry has no date to correct against.' };

  // IDI-536: a side the user did not edit (its HH:mm still equals the stored
  // stamp's) keeps the stored stamp byte-for-byte, seconds included.
  const keep = (stored, hhmm) => {
    const d = parseStamp(stored);
    return d && typeof hhmm === 'string' && hhmm.trim() && toHHMM(stored) === toHHMM(hhmm.trim())
      ? stored : null;
  };
  const keptIn = keep(entry.clockIn, clockIn);
  const inIso = keptIn || (clockIn ? combine(day, clockIn) : null);
  if (clockIn && !inIso) return { ok: false, error: 'Enter clock-in as a time, like 07:30.' };
  const startIso = inIso || (parseStamp(entry.clockIn) ? parseStamp(entry.clockIn).toISOString() : null);
  if (!startIso) return { ok: false, error: 'Clock-in is required.' };

  let endIso = null;
  if (clockOut) {
    const keptOut = keep(entry.clockOut, clockOut);
    endIso = keptOut || combine(day, clockOut);
    if (!endIso) return { ok: false, error: 'Enter clock-out as a time, like 16:30.' };
    if (new Date(endIso) <= new Date(startIso)) {
      const origIn = parseStamp(entry.originalClockIn || entry.clockIn);
      const origOut = parseStamp(entry.originalClockOut || entry.clockOut);
      const spannedMidnight = origIn && origOut && origOut.toDateString() !== origIn.toDateString();
      if (!spannedMidnight) return { ok: false, error: 'Clock-out must be after clock-in.' };
      const next = new Date(endIso);
      next.setDate(next.getDate() + 1);
      endIso = next.toISOString();
    }
  }

  // Keep the FIRST original, so a second correction doesn't overwrite it.
  const firstOriginal = (key, origKey) => (entry.corrected ? entry[origKey] : entry[key]) ?? null;
  return {
    ok: true,
    entry: {
      ...entry,
      clockIn: startIso,
      clockOut: endIso,
      corrected: true,
      originalClockIn: firstOriginal('clockIn', 'originalClockIn'),
      originalClockOut: firstOriginal('clockOut', 'originalClockOut'),
      correctedAt: now.toISOString(),
      correctedBy: by || 'Unknown',
    },
  };
}

/** Milliseconds worked by an entry, or 0 when it can't be read. */
export function entryDurationMs(entry) {
  const a = parseStamp(entry?.clockIn);
  const b = parseStamp(entry?.clockOut);
  if (!a || !b) return 0;
  const ms = b - a;
  return ms > 0 ? ms : 0;
}
